import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import Draggable from "react-draggable";
import "./FloatingAlarmTab.css";

import Assembly from "./sound/assembly.mp3";
import NSMQ from "./sound/NSMQ.mp3";
import Break from "./sound/break.mp3";
import Breakover from "./sound/breakover.mp3";
import Change from "./sound/change.mp3";
import Change2 from "./sound/change2.mp3";
import Brahms from "./sound/brahmsx27-lullaby-160672.mp3";
import Calm from "./sound/calm-and-peaceful-115481.mp3";
import Closing from "./sound/closing.mp3";
import Lunch from "./sound/lunch.mp3";
import Twinkle from "./sound/twinkle-like-a-star-8026.mp3";
import Madieval from "./sound/medieval-star-188280.mp3";
import Lunchover from "./sound/lunchover.mp3";
import Storybook from "./sound/lullaby-storybook-2-317191.mp3";
import Sleep from "./sound/lullaby-sleep-piano-music-285599.mp3";
import Fairies from "./sound/lullaby-fairies-315868.mp3";
import Baby from "./sound/lullaby-baby-sleep-dreams-celestial-music-297507.mp3";
import Legacy from "./sound/legacy-of-brahms-lullaby-background-orchestral-music-for-video-full-274550.mp3";
import Deck from "./sound/deck-the-halls-background-christmas-music-for-video-calesta-version-253224.mp3";
import silencehour from "./sound/silencehour.mp3";
import startlesson from "./sound/startlesson.mp3";
import remedial from "./sound/remedial.mp3";

function parseTimeToDate(timeStr, baseDate) {
  let hours, minutes;

  if (timeStr.includes(" ")) {
    const [time, period] = timeStr.split(" ");
    [hours, minutes] = time.split(":").map(Number);
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
  } else {
    [hours, minutes] = timeStr.split(":").map(Number);
  }

  const date = new Date(baseDate);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function computeNextAlarm(alarmList, nowDate) {
  const today = nowDate.toLocaleString("en-US", { weekday: "long" });
  const now = nowDate.getTime();
  const todayAlarms = alarmList.filter(
    (a) => a.isActive && Array.isArray(a.days) && a.days.includes(today)
  );

  const future = todayAlarms
    .map((a) => ({ ...a, alarmTime: parseTimeToDate(a.time, nowDate).getTime() }))
    .filter((a) => a.alarmTime > now)
    .sort((a, b) => a.alarmTime - b.alarmTime);

  return future[0] || null;
}

const Alarmlist = [
  { type: "assembly", alarm: Assembly },
  { type: "nsmq", alarm: NSMQ },
  { type: "break", alarm: Break },
  { type: "breakover", alarm: Breakover },
  { type: "change", alarm: Change },
  { type: "change2", alarm: Change2 },
  { type: "brahms", alarm: Brahms },
  { type: "calm", alarm: Calm },
  { type: "closing", alarm: Closing },
  { type: "lunch", alarm: Lunch },
  { type: "twinkle", alarm: Twinkle },
  { type: "madieval", alarm: Madieval },
  { type: "lunchover", alarm: Lunchover },
  { type: "storybook", alarm: Storybook },
  { type: "sleep", alarm: Sleep },
  { type: "fairies", alarm: Fairies },
  { type: "baby", alarm: Baby },
  { type: "legacy", alarm: Legacy },
  { type: "deck", alarm: Deck },
  { type: "silencehour", alarm: silencehour },
  { type: "startlesson", alarm: startlesson },
  { type: "remedial", alarm: remedial },
];

const FIRST_CLIP_MS = 30000;

const AlarmClock = () => {
  const schooldetails_ = useSelector((state) => state.schooldetails);
  const alarms = schooldetails_[0]?.siren || [];

  const [currentTime, setCurrentTime] = useState(new Date());
  const [nextAlarm, setNextAlarm] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [isMinimized, setIsMinimized] = useState(true);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [isCountdownActive, setIsCountdownActive] = useState(true);

  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const alarmSequenceTimeoutRef = useRef(null);
  const draggableRef = useRef(null);
  const firedRef = useRef(new Set());
  const soundEnabledRef = useRef(false);

  const enableSound = useCallback(() => {
    audioContextRef.current =
      audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current.resume();
    soundEnabledRef.current = true;
  }, []);

  const clearAlarmSequenceTimer = useCallback(() => {
    if (alarmSequenceTimeoutRef.current != null) {
      clearTimeout(alarmSequenceTimeoutRef.current);
      alarmSequenceTimeoutRef.current = null;
    }
  }, []);

  const playAlarmSound = useCallback(
    (alarm) => {
      if (!alarm || !alarm.isActive) return;

      if (!soundEnabledRef.current) {
        enableSound();
      }
      if (!soundEnabledRef.current) return;

      clearAlarmSequenceTimer();

      setHasTriggered(true);
      setIsCountdownActive(false);

      const soundNorm = String(alarm.sound ?? "").toLowerCase();
      const firstSrc =
        Alarmlist.find((x) => String(x.type ?? "").toLowerCase() === soundNorm)?.alarm || Deck;
      const alarmTypeNorm = String(alarm.type ?? "").toLowerCase();
      const secondSrc =
        Alarmlist.find((x) => String(x.type ?? "").toLowerCase() === alarmTypeNorm)?.alarm ||
        Deck;

      const first = new Audio(firstSrc);
      const second = new Audio(secondSrc);
      second.preload = "auto";
      second.load();

      audioRef.current = first;

      const finishSequence = () => {
        setHasTriggered(false);
        setIsCountdownActive(true);
      };

      const playSecondClip = () => {
        enableSound();
        const ctx = audioContextRef.current;
        if (ctx && ctx.state === "suspended") {
          void ctx.resume();
        }

        audioRef.current = second;

        second.onended = finishSequence;
        second.onerror = () => {
          console.error("Second audio element error");
          finishSequence();
        };

        const attempt = () =>
          second.play().catch((err) => {
            console.error("Second clip play failed:", err);
            return second.play().catch(() => finishSequence());
          });

        void attempt();
      };

      const afterFirstWindow = () => {
        clearAlarmSequenceTimer();
        try {
          first.pause();
          first.removeAttribute("src");
          first.load();
        } catch (_) {
          /* ignore */
        }
        playSecondClip();
      };

      let firstSegmentStarted = false;
      const beginFirstSegment = () => {
        if (firstSegmentStarted) return;
        firstSegmentStarted = true;

        void first
          .play()
          .then(() => {
            alarmSequenceTimeoutRef.current = setTimeout(
              afterFirstWindow,
              FIRST_CLIP_MS
            );
          })
          .catch((err) => {
            console.error("First clip play failed:", err);
            finishSequence();
          });
      };

      first.onerror = () => {
        console.error("First audio load failed");
        finishSequence();
      };

      first.oncanplaythrough = () => {
        beginFirstSegment();
      };

      if (first.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        beginFirstSegment();
      } else {
        first.load();
      }
    },
    [enableSound, clearAlarmSequenceTimer]
  );

  useEffect(() => {
    if (!isCountdownActive) return;

    const interval = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);

      const next = computeNextAlarm(alarms, now);
      setNextAlarm(next);

      if (!next) {
        setCountdown("No alarms today");
        return;
      }

      const diff = next.alarmTime - now.getTime();
      const currentMinuteKey = `${now.getHours()}:${now.getMinutes()}`;
      const eventMinuteKey = `${next.id || next.name || "alarm"}-${currentMinuteKey}`;

      for (const key of firedRef.current) {
        if (!key.endsWith(`-${currentMinuteKey}`)) {
          firedRef.current.delete(key);
        }
      }

      if (diff <= 1000 && !hasTriggered && !firedRef.current.has(eventMinuteKey)) {
        firedRef.current.add(eventMinuteKey);
        playAlarmSound(next);
        setCountdown("Alarm triggered!");
      } else {
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        setCountdown(`${h}h ${m}m ${s}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [alarms, isCountdownActive, hasTriggered, playAlarmSound]);

  useEffect(() => {
    return () => {
      if (alarmSequenceTimeoutRef.current != null) {
        clearTimeout(alarmSequenceTimeoutRef.current);
        alarmSequenceTimeoutRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  useEffect(() => {
    if (!nextAlarm) {
      draggableRef.current = null;
    }
  }, [nextAlarm]);

  return (
    <>
      {nextAlarm && (
        <Draggable handle=".panel-header" nodeRef={draggableRef}>
          <div
            ref={draggableRef}
            className={`floating-panel ${isMinimized ? "minimized" : ""}`}
            style={{ backgroundImage: "linear-gradient(315deg, #17dddd 0%, #1760d3 74%)" }}
          >
            <div className="panel-header">
              <div className="panel-title">
                Next Siren ({nextAlarm.name}: {countdown})
              </div>
              <button type="button" onClick={() => setIsMinimized(!isMinimized)}>
                {isMinimized ? "+" : "−"}
              </button>
            </div>

            <div className="panel-content">
              <h3>{nextAlarm.name}</h3>
              <p>Current: {currentTime.toLocaleTimeString()}</p>
              <p>Alarm: {nextAlarm.time}</p>
              <p>Status: {nextAlarm.isActive ? "Active" : "Inactive"}</p>
            </div>
          </div>
        </Draggable>
      )}
    </>
  );
};

export default AlarmClock;
